import { makeAutoObservable, reaction, runInAction } from 'mobx'

import { endOfMonth } from 'date-fns/endOfMonth'
import { startOfMonth } from 'date-fns/startOfMonth'
import { startOfToday } from 'date-fns/startOfToday'

import { Theme } from '@mui/system'

import { BudgetsApi } from '../api'
import { upsertByGuid } from '../utils/upsertByGuid'
import { CategoryGuids } from '../constants'
import { augmentBudgets, findBudget, findSubBudget } from '../utils/BudgetUtil'

import { GlobalStore } from './GlobalStore'

import type {
  Budget,
  Category,
  DateRange,
  DetailedBudget,
  DetailedCategory,
  MerchantBudget,
} from '../types'

export class BudgetsStore {
  api: BudgetsApi = new BudgetsApi('/', '')

  globalStore: GlobalStore

  alert: { msg: string; severity: 'success' | 'error' } | null = null
  dateRange: DateRange = { start: startOfMonth(startOfToday()), end: endOfMonth(startOfToday()) }
  budgets: Budget[] = []
  selectedBudget: DetailedBudget | undefined
  selectedSubBudget: DetailedBudget | undefined
  isDataLoaded = false
  theme = {} as Theme
  merchantBudgets: MerchantBudget[] = []

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore
    this.api = new BudgetsApi(globalStore.endpoint, globalStore.sessionToken, globalStore.onError)

    makeAutoObservable(this)
  }

  loadBudgetData = async () => {
    if (!this.globalStore.recurringTransactionsStore.isDataLoaded) {
      await this.globalStore.recurringTransactionsStore.loadRepeatingTransactionData()
    }
    await this.loadBudgets()

    /** Reaction to update the selected budget/sub-budget if detailedBudgets is updated **/
    reaction(
      () => this.detailedBudgets,
      (detailedBudgets) => {
        if (detailedBudgets) {
          const budget = this.selectedBudget
          if (budget) {
            this.selectedBudget = findBudget(detailedBudgets, budget.guid)
          }

          const subBudget = this.selectedSubBudget
          if (subBudget && subBudget.parent_guid) {
            this.selectedSubBudget = findSubBudget(
              detailedBudgets,
              subBudget.guid,
              subBudget.parent_guid,
            )
          }
        }
      },
    )
  }

  get detailedBudgets(): DetailedBudget[] {
    return augmentBudgets(
      this.budgets.filter(
        (budget: Budget) =>
          budget.category_guid !== CategoryGuids.INCOME &&
          budget.category_guid !== CategoryGuids.TRANSFER &&
          budget.category_guid !== CategoryGuids.INVESTMENTS,
      ),
      this.globalStore.categoryStore.detailedCategories,
      this.globalStore.copyStore.copy.budgets,
      this.theme,
    ).sort((a, b) => {
      if (a.percentage > b.percentage) return -1
      if (a.percentage < b.percentage) return 1
      return 0
    })
  }

  // TODO: Replace this with income from recurring transactions. Income budgets are deprecated.
  get incomeBudget(): number | undefined {
    const filteredBudgets = augmentBudgets(
      this.budgets.filter((budget: Budget) => !budget.parent_guid),
      this.globalStore.categoryStore.detailedCategories,
      this.globalStore.copyStore.copy.budgets,
      this.theme,
    )

    const incomeBudget = filteredBudgets.reduce(
      (acc, curr) => {
        if (curr.category_guid !== CategoryGuids.INCOME) return acc
        if (!acc || curr.percentage > acc.percentage) return curr
        return acc
      },
      undefined as DetailedBudget | undefined,
    )

    return incomeBudget?.amount
  }

  get unbudgetedCategories(): DetailedCategory[] {
    return this.globalStore.categoryStore.spendCategories.filter(
      (category) => !this.detailedBudgets.find((budget) => budget.category_guid === category.guid),
    )
  }

  get totalBudgeted(): number {
    return this.detailedBudgets.reduce((total, budget) => total + budget.amount, 0)
  }

  addBudget = async (category: Category, amount: number, parentGuid: string | null = null) => {
    try {
      const budget = await this.api.addBudget(category, amount, parentGuid)
      runInAction(() => {
        this.budgets = upsertByGuid(this.budgets, budget)
      })
    } catch (error) {
      console.error('Error adding budget:', error)
      this.globalStore.captureException(error, { store: 'BudgetsStore' })
    }
  }

  deleteBudget = async (guid: string): Promise<boolean> => {
    const guids = [guid, ...this.budgets.filter((b) => b.parent_guid === guid).map((b) => b.guid)]
    const results = await Promise.allSettled(guids.map((g) => this.api.deleteBudget(g)))
    const deleted = new Set(guids.filter((_, i) => results[i].status === 'fulfilled'))
    runInAction(() => {
      this.budgets = this.budgets.filter((b) => !deleted.has(b.guid))
    })
    let failed = false
    for (const [i, r] of results.entries()) {
      if (r.status === 'rejected') {
        failed = true
        console.error('Error deleting budget:', r.reason)
        this.globalStore.captureException(r.reason, { store: 'BudgetsStore', guid: guids[i] })
      }
    }
    return !failed
  }

  loadBudgets = async () => {
    try {
      const budgets = await this.api.getBudgets()
      this.setBudgets(budgets)
    } catch (error) {
      console.error('Error occurred while loading budgets:', error)
      this.globalStore.captureException(error, { store: 'BudgetsStore' })
      throw error
    }
  }

  setAlert = (alert: { msg: string; severity: 'success' | 'error' } | null) => (this.alert = alert)

  setBudgets = (budgets: Budget[]) => {
    this.budgets = budgets
    this.isDataLoaded = true
  }

  setDateRange = (dateRange: DateRange) => (this.dateRange = dateRange)

  setSelectedBudget = (budget: DetailedBudget | undefined) => (this.selectedBudget = budget)

  setSelectedSubBudget = (budget: DetailedBudget | undefined) => (this.selectedSubBudget = budget)

  setTheme = (theme: Theme) => (this.theme = theme)

  /**
   * Recalculate budget by using the average amount from top-level categories
   * @param categories Detailed categories
   */
  recalculateBudgets = async (categories: DetailedCategory[]) => {
    try {
      for (const category of categories) {
        const budget = this.budgets.find((budget) => budget.category_guid === category.guid)
        if (budget) {
          await this.api.updateBudget({
            ...budget,
            amount: category.totalAverageAmount,
          })
        } else {
          await this.api.addBudget(category, category.totalAverageAmount)
        }
      }
      const budgetsToDelete = this.budgets.filter(
        (budget) => !categories.find((c) => c.guid === budget.category_guid),
      )

      for (const budgetToDelete of budgetsToDelete) {
        await this.api.deleteBudget(budgetToDelete.guid)
      }

      await this.loadBudgets()

      return { data: this.budgets, isSuccess: true }
    } catch (error) {
      console.error('Error recalculating budgets:', error)
      this.globalStore.captureException(error, { store: 'BudgetsStore' })
      return { data: [], isSuccess: false }
    }
  }

  updateBudget = async (budget: Budget): Promise<boolean> => {
    try {
      const updatedBudget = await this.api.updateBudget(budget)

      runInAction(() => {
        const index = this.budgets.findIndex((b) => b.guid === budget.guid)
        if (index >= 0) {
          this.budgets[index] = updatedBudget
        }
      })
      return true
    } catch (error) {
      console.error('Error updating budget:', error)
      this.globalStore.captureException(error, { store: 'BudgetsStore' })
      return false
    }
  }

  getMerchantBudgetByMerchantGuid = (merchantGuid: string): MerchantBudget | undefined => {
    return this.merchantBudgets.find(
      (eachMerchantBudget) => eachMerchantBudget?.merchant_guid === merchantGuid,
    )
  }

  setMerchantBudgets = (merchantBudgets: MerchantBudget[]) => {
    this.merchantBudgets = merchantBudgets
  }

  addMerchantBudget = async (merchantGuid: string, amount: number) => {
    try {
      const newMerchantBudget = await this.api.addMerchantBudget(merchantGuid, amount)

      runInAction(() => {
        this.merchantBudgets = upsertByGuid(this.merchantBudgets, newMerchantBudget)
      })
      return { isSuccess: true }
    } catch (error) {
      console.error('Error adding merchant budget:', error)
      this.globalStore.captureException(error, { store: 'BudgetsStore' })
      return { isSuccess: false }
    }
  }

  updateMerchantBudget = async (merchantBudget: MerchantBudget) => {
    try {
      const { guid: merchantBudgetGuid, amount } = merchantBudget
      const updatedMerchantBudget = await this.api.updateMerchantBudget(merchantBudgetGuid, amount)

      runInAction(() => {
        const index = this.merchantBudgets.findIndex((b) => b.guid === merchantBudget.guid)
        if (index >= 0) {
          this.merchantBudgets[index] = updatedMerchantBudget
        }
      })
      return { isSuccess: true }
    } catch (error) {
      console.error('Error updating merchant budget:', error)
      this.globalStore.captureException(error, { store: 'BudgetsStore' })
      return { isSuccess: false }
    }
  }

  deleteMerchantBudget = async (merchantBudgetGuid: string) => {
    try {
      await this.api.deleteMerchantBudget(merchantBudgetGuid)

      runInAction(() => {
        this.merchantBudgets = this.merchantBudgets.filter((b) => b.guid !== merchantBudgetGuid)
      })
      return { isSuccess: true }
    } catch (error) {
      console.error('Error deleting merchant budget:', error)
      this.globalStore.captureException(error, { store: 'BudgetsStore' })
      return { isSuccess: false }
    }
  }
}
